/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

import { useMemo } from "react"
import { useSelector } from "react-redux"
import { AppState } from "store/store.type"
import { Department } from "types/models/menu"
import { mapItemDepartmentsToDepartments } from "utils/menuV3Mapper"
import {
  getItemWithoutPhotoCountFromDepartment,
  isItemWithoutPhotoMode,
} from "utils/menu_helper"
import { useMartMenuDisplayQuery } from "./useMartMenuDisplayQuery"

interface UseMenuOverviewDepartmentsReturn {
  filteredDepartments: Department[]
  isLoading: boolean
  isFetching: boolean
}

export const useMenuOverviewDepartments = (): UseMenuOverviewDepartmentsReturn => {
  const {
    enablePartialMenu,
    martMenuData,
    isQueryLoading,
    isQueryFetching,
  } = useMartMenuDisplayQuery()

  // Get data from Redux
  const {
    overview: { departments: reduxDepartments, sellingTime },
    itemFilter,
  } = useSelector((state: AppState) => state?.menu)

  // Get departments from query or Redux
  const departments = useMemo(() => {
    if (enablePartialMenu && martMenuData) {
      return mapItemDepartmentsToDepartments(martMenuData.departments || [])
    }
    return reduxDepartments
  }, [enablePartialMenu, martMenuData, reduxDepartments])

  // Filter departments by selling time and item without photo
  const filteredDepartments = useMemo(() => {
    let result = departments

    // Filter by selling time
    if (sellingTime) {
      result = result
        .map(department => {
          // Filter sub-departments that have items matching the selling time
          const filteredSubDepartments = department.subDepartments
            .filter(subDept =>
              subDept.items?.some(
                item => item.sellingTimeID === sellingTime.sellingTimeID,
              ),
            )
            .map(subDept => ({
              ...subDept,
              items: subDept.items?.filter(
                item => item.sellingTimeID === sellingTime.sellingTimeID,
              ),
            }))
          // Only include department if it has filtered sub-departments
          return filteredSubDepartments.length > 0
            ? { ...department, subDepartments: filteredSubDepartments }
            : null
        })
        .filter((dept): dept is Department => dept !== null)
    }

    // Filter by item without photo
    if (isItemWithoutPhotoMode(itemFilter)) {
      result = result.filter(department => {
        return getItemWithoutPhotoCountFromDepartment(department) > 0
      })
    }

    return result
  }, [departments, itemFilter, sellingTime])

  // Determine loading state: use query loading when partial menu is enabled
  const isLoading = enablePartialMenu
    ? isQueryLoading || (isQueryFetching && !martMenuData)
    : false

  return {
    filteredDepartments,
    isLoading,
    isFetching: enablePartialMenu ? isQueryFetching : false,
  }
}
